import React from 'react';
import { makeStyles } from 'react-scale/components/design-system/styles';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { Button } from 'design-system';
import { MentionsInput, Mention, SuggestionDataItem } from 'react-mentions';
import ReactTooltip from 'react-tooltip';
import { FEEDBACK_MIN_CHARS } from '@scale/shared/selfServe/calibrationScore';

/* Autocomplete textarea */

interface AutocompleteProps {
  data: SuggestionDataItem[];
  value?: string;
  onChange?: any;
  onAdd?: any;
}

const AutocompleteTextArea: React.FC<AutocompleteProps> = props => {
  const inputEl = React.createRef<HTMLTextAreaElement>();

  const placeholder = `Tip: this is slightly different from an Experiment Task. You must write good feedback in order to get the $3 bonus.

You can use '@' to mention specific labels

    `;

  return (
    <>
      <div style={{ height: '400px' }}>
        <MentionsInput
          style={{
            height: '400px',

            suggestions: {
              list: {
                backgroundColor: 'white',
                border: '1px solid rgba(0,0,0,0.15)',
                fontSize: 14,
              },
              item: {
                padding: '0px 12px',
                borderBottom: '1px solid rgba(0,0,0,0.15)',
                '&focused': {
                  backgroundColor: '#cee4e5',
                },
              },
            },
          }}
          value={props.value}
          onChange={props.onChange}
          placeholder={placeholder}
          inputRef={inputEl}
        >
          <Mention
            appendSpaceOnAdd
            trigger="@"
            displayTransform={(login: $FIXME) => `@${login}`}
            data={props.data}
            onAdd={props.onAdd}
            markup={'@[__display__]'}
          />
        </MentionsInput>
      </div>
      <samp></samp>
    </>
  );
};

/* Main instruction feedback form */

interface InstructionFeedbackFormProps {
  taskTimer?: any;
  // display props
  subtask: string;
  onClose: () => void;
  setSubmit: () => void;
  labels: string[];
  originalText: string;

  // props to edit text
  text: string;
  onChange: any;
  onAdd: any;

  // props to edit arr
  taggedLabels: string[];

  feedbackRequired?: boolean;
  readOnly?: boolean;
}

const DetailedFeedbackForm: React.FC<InstructionFeedbackFormProps> = ({
  subtask,
  onClose,
  setSubmit,
  labels,
  onChange,
  onAdd,
  text,
  taggedLabels,
  feedbackRequired = false,
  readOnly = false,
}) => {
  const classes = useStyles();

  const onSubmit = async () => {
    // 0.5 for undefined is maintained for backwards compatibility
    // previously submitted feedback doesn't have a confidence assessment

    // in case labels were deleted, check for label in set inside feedback string
    const filteredTaggedLabels: string[] = taggedLabels.filter(label => text.includes(label));

    const feedback: Record<string, any> = {
      text,
      confidence: 1,
      taggedLabels: filteredTaggedLabels,
    };

    const body = {
      subtask,
      feedback: JSON.stringify(feedback),
    };
    await apiFetch(null, `internal/self_serve/instruction_feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setSubmit();
    onClose();
  };

  const labelArray = labels.map(label => {
    return { id: label, display: label };
  });

  const lengthWarning =
    feedbackRequired && text.trim().length < FEEDBACK_MIN_CHARS
      ? 'Please write more about your thinking process'
      : undefined;

  const renderForm = () => {
    return (
      <div>
        <div>
          <div style={{ fontSize: 16 }}>You got a feedback task! 📝</div>
          <div style={{ marginTop: 8, fontSize: 14, opacity: 0.5 }}>
            <div style={{ marginBottom: 8 }}>
              The main goal if this task is to provide detailed feedback about the task
              instructions. If your feedback is accepted, you will be awarded a bonus of $3.
            </div>
            Steps for writing great feedback:
            <ol>
              <li>Read the instructions carefully</li>
              <li>Try to label the task</li>
              <li>Was anything unclear? What can be improved about the instructions?</li>
              <li>
                Provide <b>detailed</b> feedback in complete sentences. Aim for at least 5
                sentences.
              </li>
            </ol>
          </div>
          <div className={classes.section}>
            <div className={classes.sectionTitle}>Instruction Feedback</div>
            <AutocompleteTextArea
              data={labelArray}
              value={text}
              onChange={onChange}
              onAdd={onAdd}
            />
          </div>
          <div className={classes.section}>
            {!readOnly && (
              <div
                data-tip
                data-for="submit-disabled"
                style={{
                  marginTop: 8,
                  display: 'inline',
                }}
              >
                <Button onClick={() => onSubmit()} disabled={lengthWarning !== undefined}>
                  Submit
                </Button>
              </div>
            )}
            {lengthWarning && (
              <ReactTooltip id="submit-disabled" type="dark">
                <div style={{ fontWeight: 'bold' }}>{lengthWarning}</div>
              </ReactTooltip>
            )}
            <Button
              onClick={() => {
                onClose();
              }}
              disabled={false}
              style={{ marginLeft: 4 }}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return <div className={classes.instructionFeedbackForm}>{renderForm()}</div>;
};

const useStyles = makeStyles({
  instructionFeedbackForm: {
    padding: '16px',
    alignItems: 'center',
    backgroundColor: 'white',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    position: 'absolute',
    bottom: '0',
    right: '0',
    zIndex: 9,
    width: 'min(100%, 800px)',
  },
  sectionTitle: {
    fontWeight: 'bold',
    padding: '4px',
  },
  section: {
    padding: '4px',
  },
  buttonInnerWrapper: {
    width: '-webkit-fill-available',
    boxSizing: 'border-box',
    backgroundColor: 'white',
    padding: '4px',
    marginTop: '8px',
    marginBottom: '8px',
    borderRadius: '15px',
  },
});

export default DetailedFeedbackForm;
